import { useCallback, useEffect, useRef } from 'react';
import { createBridgeClient } from '@atlassian/rovo-content-bridge-api/create-bridge-client';
import type { RovoContentBridgeAPIShape } from '@atlassian/rovo-content-bridge-api/rovo-bridge-api-interface';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import {
	AiPlannerBackCommand,
	AiPlannerCloseCommand,
	AiPlannerLoadPlanCommand,
	AiPlannerNavigateCommand,
	AiPlannerPrewarmPlansCommand,
	type AiPlannerTransitionState,
	AiPlannerTransitionCommand,
	getAiPlannerEmbedClientId,
	getAiPlannerEmbedHostClientId,
} from '@atlassian/ai-planner-embed-bridge/AiPlannerBridgeCommands';

/** A transition signalled by the planner iframe for a given request. */
export type AiPlannerTransition = {
	requestId: string;
	state: AiPlannerTransitionState;
	isLoading: boolean;
};

type UseAiPlannerBridgeArgs = {
	/** Called whenever the planner reports a lifecycle transition. */
	onTransition: (transition: AiPlannerTransition) => void;
	/**
	 * Called when the planner asks the host to perform shell-level back
	 * navigation (the user pressed the in-planner back button).
	 */
	onBack?: () => void;
	/**
	 * Called when the planner asks the host to dismiss/close the embed (the user
	 * pressed the in-planner close button).
	 */
	onClose?: () => void;
	/**
	 * Called when the planner asks the host to navigate the top-level Jira page.
	 * The host owns destination validation and navigation.
	 */
	onNavigate?: (url: string) => void;
	/**
	 * Per-iframe bridge instance id. When set, the host derives a private Rovo
	 * bridge channel (scoped host/embed ids) so this embed never collides with
	 * another planner iframe
	 */
	bridgeInstanceId?: string;
};

export type AiPlannerBridge = {
	/**
	 * Ask the planner iframe to navigate to a plan (or the root when `planId` is
	 * omitted) without reloading. Returns the `requestId` used, which the planner
	 * echoes back on its `ready` transition.
	 *
	 * If the planner has not yet announced itself (initial `idle` transition not
	 * received), the request is queued and flushed once the planner is ready, so
	 * commands are never dropped for being sent too early.
	 */
	sendLoadPlan: (planId?: string) => string;
	/**
	 * Ask the planner to warm the full-plan cache for the given plan ids ahead of
	 * a likely navigation (e.g. recent plans on warm-up, or a hovered row). The
	 * planner dedupes, caps concurrency and silently ignores ids it can't
	 * resolve, so this is safe to call generously. Best-effort and non-blocking:
	 * unknown ids, a lost message or a not-yet-ready planner are no-ops, and a
	 * later `sendLoadPlan` still fetches normally.
	 */
	sendPrewarmPlans: (planIds: readonly string[]) => void;
};

let requestCounter = 0;
const nextRequestId = (): string => {
	requestCounter += 1;
	return `jira-ai-planner-${requestCounter}`;
};

/**
 * Establishes the Rovo Content Bridge client used to drive the embedded AI
 * planner iframe: registers a handler for the planner's `transition` command and
 * exposes `sendLoadPlan` for in-iframe navigation.
 *
 * The bridge is best-effort — if it cannot be created (SSR, no window, library
 * unavailable) the returned `sendLoadPlan` is a safe no-op so the caller never
 * crashes the page.
 */
export const useAiPlannerBridge = ({
	onTransition,
	onBack,
	onClose,
	onNavigate,
	bridgeInstanceId,
}: UseAiPlannerBridgeArgs): AiPlannerBridge => {
	// Derive the bridge identities from the per-iframe instance id
	const hostClientId = getAiPlannerEmbedHostClientId(bridgeInstanceId);
	const embedClientId = getAiPlannerEmbedClientId(bridgeInstanceId);

	const apiRef = useRef<RovoContentBridgeAPIShape | null>(null);
	// Keep the latest callbacks without re-creating the client on every render.
	const onTransitionRef = useRef(onTransition);
	const onBackRef = useRef(onBack);
	const onCloseRef = useRef(onClose);
	const onNavigateRef = useRef(onNavigate);
	useEffect(() => {
		onTransitionRef.current = onTransition;
		onBackRef.current = onBack;
		onCloseRef.current = onClose;
		onNavigateRef.current = onNavigate;
	}, [onTransition, onBack, onClose, onNavigate]);

	// True once the planner has announced itself (first transition received).
	// Until then, loadPlan requests are buffered to honour the contract's
	// "wait for the initial idle before sending the first loadPlan".
	const plannerReadyRef = useRef(false);
	// The most recent plan requested before the planner was ready (last wins).
	const pendingPlanRef = useRef<{ requestId: string; planId?: string } | null>(null);
	// Prewarm ids requested before the planner was ready. Unlike loadPlan these
	// accumulate (a union, deduped) and are flushed together on the first
	// transition, so hovers during the iframe's cold boot are not lost.
	const pendingPrewarmIdsRef = useRef<Set<string>>(new Set());

	const dispatchLoadPlan = useCallback(
		(requestId: string, planId?: string) => {
			const api = apiRef.current;
			if (api == null) {
				return;
			}
			try {
				api.send(AiPlannerLoadPlanCommand, {
					senderId: hostClientId,
					receiverId: embedClientId,
					requestId,
					planId,
					// The host owns back and close behaviour. Advertise each capability only
					// when the current embed surface can honour it; capabilities are read
					// from the latest loadPlan, so we send them every time.
					capabilities: {
						canBack: onBackRef.current != null,
						canClose: onCloseRef.current != null,
						canNavigate: onNavigateRef.current != null,
					},
				});
			} catch {
				// Swallow — the planner may not be registered yet.
			}
		},
		[hostClientId, embedClientId],
	);

	const dispatchPrewarm = useCallback(
		(planIds: string[]) => {
			const api = apiRef.current;
			if (api == null || planIds.length === 0) {
				return;
			}
			try {
				// Best-effort and fire-and-forget — the host never blocks on the
				// `{ accepted: true }` result.
				api.send(AiPlannerPrewarmPlansCommand, {
					senderId: hostClientId,
					receiverId: embedClientId,
					planIds,
				});
			} catch {
				// Swallow — prewarm is best-effort; loadPlan will fetch on click.
			}
		},
		[hostClientId, embedClientId],
	);

	const sendPrewarmPlans = useCallback(
		(planIds: readonly string[]) => {
			// Trim, drop empties and dedupe so repeated hover prewarms stay cheap and
			// we never send a degenerate message. The planner dedupes too, but
			// keeping the wire payload tidy avoids relying on it.
			const seenIds = new Set<string>();
			const uniqueIds: string[] = [];
			for (const rawId of planIds) {
				const id = rawId.trim();
				if (id !== '' && !seenIds.has(id)) {
					seenIds.add(id);
					uniqueIds.push(id);
				}
			}
			if (uniqueIds.length === 0) {
				return;
			}
			if (plannerReadyRef.current) {
				dispatchPrewarm(uniqueIds);
				return;
			}
			// Planner not ready yet (e.g. first hover during the iframe's cold
			// boot). Buffer the ids — accumulating a deduped union — and flush them
			// once the planner announces itself, so the warm is never lost.
			for (const id of uniqueIds) {
				pendingPrewarmIdsRef.current.add(id);
			}
		},
		[dispatchPrewarm],
	);

	useEffect(() => {
		if (typeof globalThis.window === 'undefined') {
			return undefined;
		}
		// Capture the (stable, ref-held) prewarm buffer so the cleanup clears the
		// same Set instance the effect used, satisfying react-hooks/exhaustive-deps.
		const pendingPrewarmIds = pendingPrewarmIdsRef.current;
		let api: RovoContentBridgeAPIShape | null = null;
		try {
			api = createBridgeClient(hostClientId, getBrowserBridgeAPI(), (on) => {
				on(AiPlannerTransitionCommand, (props) => {
					// The planner is now listening: flush any queued navigation and
					// any prewarms buffered during the iframe's cold boot.
					if (!plannerReadyRef.current) {
						plannerReadyRef.current = true;
						const pending = pendingPlanRef.current;
						pendingPlanRef.current = null;
						if (pending != null) {
							dispatchLoadPlan(pending.requestId, pending.planId);
						}
						const pendingPrewarms = [...pendingPrewarmIdsRef.current];
						pendingPrewarmIdsRef.current.clear();
						dispatchPrewarm(pendingPrewarms);
					}
					onTransitionRef.current({
						requestId: props.requestId,
						state: props.state,
						isLoading: props.isLoading,
					});
				});
				// Payload-less intents: the planner signals, the host owns the
				// behaviour (shell-level back / dismissing the embed).
				on(AiPlannerBackCommand, () => {
					onBackRef.current?.();
				});
				on(AiPlannerCloseCommand, () => {
					onCloseRef.current?.();
				});
				on(AiPlannerNavigateCommand, ({ url }) => {
					onNavigateRef.current?.(url);
				});
			});
			apiRef.current = api;
		} catch {
			// Bridge unavailable — leave sendLoadPlan as a no-op.
			apiRef.current = null;
		}
		return () => {
			try {
				api?.teardown(hostClientId);
			} catch {
				// best-effort teardown
			}
			apiRef.current = null;
			plannerReadyRef.current = false;
			pendingPlanRef.current = null;
			pendingPrewarmIds.clear();
		};
	}, [dispatchLoadPlan, dispatchPrewarm, hostClientId]);

	const sendLoadPlan = useCallback(
		(planId?: string): string => {
			const requestId = nextRequestId();
			if (plannerReadyRef.current) {
				dispatchLoadPlan(requestId, planId);
			} else {
				// Planner not ready yet — buffer the latest request (last wins).
				pendingPlanRef.current = { requestId, planId };
			}
			return requestId;
		},
		[dispatchLoadPlan],
	);

	return { sendLoadPlan, sendPrewarmPlans };
};
